<template>
  <div class="nav-footer">
    <div
      v-if="
        workspaceStore.subscriptionLoaded && !workspaceStore.isSubscribedState
      "
      class="upgrade-box"
    >
      <span class="plan-label">Free Plan</span>
      <p class="upgrade-message">
        <strong>{{ remainingFiles }}</strong> free
        {{ remainingFiles === 1 ? "file" : "files" }} remaining today. <br />
        Upgrade to a paid plan to upload more.
      </p>
      <router-link to="/settings/plans" class="btn" @click="trackUpgradeClicked"
        >Upgrade now</router-link
      >
    </div>
    <ul class="user-list">
      <li title="Help">
        <a
          href="https://audioscribe.io/help"
          target="_blank"
          rel="noopener noreferrer"
          class="icon-doubts-button"
          aria-label="Help"
          @click="trackHelpClicked"
        ></a>
      </li>
      <li title="Settings">
        <router-link to="/settings" class="icon-setting"></router-link>
      </li>
      <logoutButton></logoutButton>
    </ul>
    <div class="centered-content">
      <span v-if="appStore?.user?.email" class="footer-email">{{
        appStore?.user?.email
      }}</span>
    </div>
  </div>
</template>

<script>
import { useWorkSpaceStore } from "@/stores/workspace-store";
import logoutButton from "./LogoutButton.vue";
import { useAppStore } from "@/stores/app-store";
import { mapStores } from "pinia";
import { analytics } from "@/services/analytics.service";

export default {
  name: "NavFooter",
  components: {
    logoutButton,
  },
  computed: {
    ...mapStores(useAppStore),
    ...mapStores(useWorkSpaceStore),
    // Computed property for isSubscribed
    isSubscribed() {
      return this.workspaceStore.isSubscribedState;
    },
    // Computed properties for file limit data
    hasExceededLimit() {
      return this.workspaceStore.hasExceededFileUploadLimit;
    },
    remainingFiles() {
      return this.workspaceStore.remainingFiles;
    },
  },
  data() {
    return {
      intervalId: null,
    };
  },
  mounted() {
    // console.log("Called the mounted method of NavFooter");
    this.checkSubscription();
  },
  methods: {
    async checkSubscription() {
      try {
        // Free users get a lightweight 20s poll of the daily-upload limit;
        // subscribed users don't need it. The isSubscribed watcher below keeps
        // this in sync if the plan changes while the app stays mounted.
        if (this.workspaceStore.isSubscribedState) {
          this.stopLimitPolling();
        } else {
          this.startLimitPolling();
        }
      } catch (error) {
        console.error("Failed to check subscription status", error);
      }
    },
    startLimitPolling() {
      // Never stack intervals — clear any existing one before starting.
      this.stopLimitPolling();
      this.checkFileUploadLimit(); // Initial check
      this.intervalId = setInterval(this.checkFileUploadLimit, 20000);
    },
    stopLimitPolling() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    async checkFileUploadLimit() {
      try {
        // console.log("Checking file upload limit via store");
        await this.workspaceStore.checkDailyFileUploadLimit();
      } catch (error) {
        console.error("Failed to check file upload limit", error);
      }
    },
    trackUpgradeClicked() {
      analytics.navUpgradeClicked({
        remaining_files: this.remainingFiles,
      });
    },
    trackHelpClicked() {
      analytics.navHelpClicked();
    },
  },
  beforeUnmount() {
    this.stopLimitPolling();
  },
  watch: {
    // When the plan changes while the app stays mounted (e.g. the user
    // upgrades), start or stop the free-tier limit poll to match. This
    // previously watched `workspaceStore.checkUserSubscription` — a property
    // that does not exist on the store — so it never fired and the 20s poll
    // leaked for the rest of the session after a user subscribed.
    isSubscribed(subscribed) {
      if (subscribed) {
        this.stopLimitPolling();
      } else {
        this.startLimitPolling();
      }
    },
    remainingFiles(newVal, oldVal) {
      // Track warning when down to 1 file
      if (newVal === 1 && oldVal > 1) {
        analytics.limitFileUploadWarning({
          remaining_uploads: newVal,
        });
      }
      // Track limit reached when no files left
      if (newVal === 0 && oldVal > 0) {
        analytics.limitFileUploadReached({
          daily_limit: 3,
          files_uploaded_today: 3,
        });
      }
    },
  },
};
</script>

<style scoped>
.nav-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.upgrade-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
  width: 100%;
  max-width: 300px;
}

.plan-label {
  font-weight: 600;
  color: #0d2a3a;
  margin-bottom: 8px;
}

.upgrade-message {
  font-size: 14px;
  margin: 0 0 16px 0;
}

.upgrade-button {
  background-color: #f16622;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
  transition: background-color 0.3s ease;
}

.upgrade-button:hover {
  background-color: #3700b3;
}

.user-list {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.footer-email {
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

@media (min-width: 1024px) {
  .footer-email {
    font-size: 10px;
  }
}

@media (min-width: 1366px) {
  .footer-email {
    font-size: 12px;
  }
}
</style>
